import UAParser from 'ua-parser-js';
import { getLocale } from '@snapchat/web-i18n';
import type { UserTrackedEvent } from '@snapchat/blizzard-schema';
import { getAppEnvironment, type WebAppType } from '../environment';
import { getSessionProperties } from '../lifecycle/session';
import isEqual from 'lodash/isEqual.js';
import { getClientId } from '../clientId';
import { getAppBuild, getReferrer } from './eventMetadata';
import { getWebSessionId } from '../webSession/webSessionId';

/**
 * The FrameStart represents infrequently changing common base metadata shared across all events.
 * The server (or the client, if sending JSON) denormalizes these with the event-specific fields
 * into a single event object.
 *
 * These are a subset of the fields defined on EventBase (some are unused, some are set at a
 * per-event basis).
 *
 * @see https://github.sc-corp.net/Snapchat/snapchat/blob/master/blizzard/schema/blizzard-schema/codeGen/src/main/java/com/snapchat/analytics/schema/events/EventBase.java This will eventually be converted into the actual proto representation.
 * @see https://livegrep.sc-corp.net/view/Snapchat/pb_schema/proto/data/framedevent.proto#L108
 */

/** @internal */
export type FrameStart = Pick<
  UserTrackedEvent,
  | 'client_id'
  | 'session_id'
  | 'app_startup_type'
  | 'user_agent'
  | 'locale'
  | 'app_build'
  | 'app_version'
  | 'os_type'
  | 'os_version'
  | 'device_model'
  | 'browser'
  | 'browser_version'
  | 'referrer'
  | 'domain'
  | 'user_guid'
  | 'log_queue_name'
  | 'blizzard_web_session_id'
> & {
  app_type?: WebAppType;
};

// TODO: fix these types in followup PR changing to use AnyEvent everywhere
export const isFrameStart = (item: object): item is FrameStart =>
  (item as FrameStart).user_agent !== undefined;

/**
 * Retrieves a new {@link FrameStart}.
 *
 * @internal
 */
let currentFrameStart: FrameStart | undefined;

/** TODO(kho): TSDoc */
export async function getFrameStart(): Promise<FrameStart | null> {
  const userGuid = await userGuidPromise;
  partialFrameStart ??= getPartialFrameStart();
  const newFrameStart = {
    ...partialFrameStart,

    client_id: getClientId(),

    // Log session metadata (undefined unless explicitly enabled via setSessionsEnabled)
    session_id: getSessionProperties()?.sessionId,
    app_startup_type: getSessionProperties()?.appStartupType,
    blizzard_web_session_id: getWebSessionId(),

    user_guid: userGuid ?? undefined,
  };

  if (!isEqual(currentFrameStart, newFrameStart)) {
    currentFrameStart = newFrameStart;
    return newFrameStart;
  }

  return null;
}

// The fixed, unchanging, fields of the FrameStart, cached.
const osTypesMap: Record<string, string> = { 'MAC OS': 'OSX' };
let partialFrameStart: Partial<FrameStart>;

/** - TODO(kho): TSDoc */
function getPartialFrameStart(): Partial<FrameStart> {
  const uaParser = new UAParser();
  const osName = uaParser.getOS().name?.toUpperCase();
  const osType = osName ? osTypesMap[osName] || osName : undefined;
  return {
    user_agent: uaParser.getUA(),

    locale: getLocale(),

    os_type: osType,
    os_version: osType, // TODO: do we need this? (spec defines os_type but old code was sending os_version)

    app_type: getAppEnvironment().appType,
    app_build: getAppBuild(),
    app_version: getAppEnvironment().appVersion,

    browser: uaParser.getBrowser().name,
    browser_version: uaParser.getBrowser().version,

    device_model: uaParser.getDevice().model,

    domain: self.location.hostname,

    referrer: getReferrer(),
  };
}

let userGuidPromise: Promise<string | null> = Promise.resolve(null);
/** @internal */
export function getUserGuidPromise(): Promise<string | null> {
  return userGuidPromise;
}

/** - TODO(kho): TSDoc */
export function setUserGuidPromise(promise: Promise<string | null>): void {
  userGuidPromise = promise;
}
